import React from 'react';
import { EmptyTableState, TableSkeleton } from './EmptyState';

export interface Column<T> {
  header: string;
  accessor: keyof T | ((item: T) => React.ReactNode);
  className?: string;
  width?: string; // Ajouter une option de largeur pour les colonnes
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  emptyTitle?: string;
  emptyAction?: {
    label: string;
    onClick: () => void;
    icon?: 'document' | 'plus' | 'refresh' | 'database' | 'search' | 'filter';
  };
  onRowClick?: (item: T) => void;
  rowClassName?: (item: T) => string;
  containerClassName?: string; // Classe personnalisée pour le conteneur
  tableClassName?: string; // Classe personnalisée pour la table
}

export function Table<T extends { id: string | number }>({ 
  columns, 
  data, 
  loading = false,
  emptyMessage = "Aucune donnée disponible",
  emptyTitle = "Aucune donnée",
  emptyAction,
  onRowClick,
  rowClassName,
  containerClassName,
  tableClassName
}: TableProps<T>) {
  // Protection pour les données undefined/null avec préservation des types
  const safeData = Array.isArray(data) ? data.filter(item => item != null && item.id != null) : [];
  const safeColumns = Array.isArray(columns) ? columns : [];
  
  return (
    <div className={`table-container overflow-visible ${containerClassName || ''}`}>
      <table className={`table w-full ${tableClassName || ''}`}>
        <thead>
          <tr>
            {safeColumns.map((column, index) => (
              <th
                key={index}
                className={`${column.className || ''}`}
                style={column.width ? { width: column.width, minWidth: column.width } : { width: 'auto' }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody> 
          {loading ? (
            <TableSkeleton rows={5} columns={safeColumns.length} />
          ) : safeData.length === 0 ? (
            <EmptyTableState
              title={emptyTitle}
              description={emptyMessage}
              colSpan={safeColumns.length}
              action={emptyAction}
            />
          ) : (
            safeData.map((item) => (
              <tr 
                key={item.id}
                onClick={() => onRowClick?.(item)}
                className={`
                  ${rowClassName ? rowClassName(item) : ''}
                  ${onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''} 
                `}
              >
                {safeColumns.map((column, colIndex) => {
                  const isLastColumn = colIndex === safeColumns.length - 1;
                  return (
                    <td
                      key={colIndex}
                      className={`${column.className || ''} ${isLastColumn ? 'overflow-visible' : ''}`}
                      style={column.width ? { width: column.width, minWidth: column.width } : { width: 'auto' }}
                    >
                      {typeof column.accessor === 'function'
                        ? column.accessor(item)
                        : String(item[column.accessor])}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}